import { AlertCircle, CheckCircle, Pencil } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  FixedNumericInput,
  LinkText,
  Logo,
  Text,
  XStack,
  YStack,
} from "@/components";
import BackButton from "./BackButton";
import { FORMS, SIGN_IN_FLOW } from "./SignIn";
import {
  CODE_LENGTH,
  RESEND_TIMER_SECONDS,
  formatPhone,
  usePhoneCodeAuth,
} from "./usePhoneCodeAuth";

export interface OTPFormProps {
  phone: string;
  setForm: (form: FORMS) => void;
  signInFlowRef: React.RefObject<SIGN_IN_FLOW>;
  isEmbedded?: boolean;
}

type OTP_STATUS = "incorrect" | "correct" | null;

const OTPStatus = ({ status }: { status: OTP_STATUS }) => {
  const sharedStyle = {
    position: "absolute" as const,
    bottom: "$-300",
    left: 0,
    right: 0,
    justifyContent: "center" as const,
    alignItems: "center" as const,
    gap: "$50" as const,
  };
  return (
    <>
      <XStack {...sharedStyle} role="status" aria-live="polite" aria-atomic>
        {status === "correct" && (
          <>
            <CheckCircle color="$green" size="$200" flexShrink={0} />
            <Text size="$sm">Success</Text>
          </>
        )}
      </XStack>
      <XStack {...sharedStyle} role="alert" aria-live="assertive" aria-atomic>
        {status === "incorrect" && (
          <>
            <AlertCircle color="$red" size="$200" flexShrink={0} />
            <Text size="$sm">Incorrect code</Text>
          </>
        )}
      </XStack>
    </>
  );
};

export default function OTPForm({
  phone,
  setForm,
  signInFlowRef,
  isEmbedded,
}: OTPFormProps) {
  const { verifyCode, resendCode, activateSession } =
    usePhoneCodeAuth(signInFlowRef);
  const { show } = useToastController();

  const [disableInput, setDisableInput] = useState(false);

  const [otp, setOtp] = useState("");
  const [otpStatus, setOtpStatus] = useState<OTP_STATUS>(null);
  const [timeToResend, setTimeToResend] = useState(RESEND_TIMER_SECONDS);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (timeToResend > 0) {
      timerRef.current = setInterval(() => {
        setTimeToResend((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeToResend]);

  const onContinue = useCallback(
    async (value: string) => {
      setDisableInput(true);
      const result = await verifyCode(value);
      if (result.outcome === "verified") {
        setOtpStatus("correct");
        setTimeout(() => {
          // Show the otp status borders for a little while before changing to loading screen
          setForm("loading");
        }, 500);
        setTimeout(async () => {
          // Show the loading screen for 1s after the otp status clears before doing the log in
          await activateSession(result.sessionId);
        }, 1500);
        return;
      }

      setDisableInput(false);
      if (result.outcome === "incorrect") {
        setOtpStatus("incorrect");
        setTimeout(() => {
          setOtp("");
          setOtpStatus(null);
        }, 1500);
        return;
      }
      if (result.errorMessage) {
        show(result.errorMessage, { duration: 10000 });
      }
    },
    [verifyCode, activateSession, setForm, show],
  );

  const resendElement =
    timeToResend > 0 ? (
      `Resend (${timeToResend})`
    ) : (
      <LinkText
        size="$xs"
        bold
        onPress={async () => {
          setOtp("");
          setOtpStatus(null);
          setTimeToResend(RESEND_TIMER_SECONDS);
          const errorMessage = await resendCode();
          if (errorMessage) {
            show(errorMessage, { duration: 10000 });
          }
        }}
      >
        Resend
      </LinkText>
    );

  let inputBorderColor = "$strokeNeutral";
  if (otpStatus === "incorrect") {
    inputBorderColor = "$red";
  } else if (otpStatus === "correct") {
    inputBorderColor = "$green";
  }

  return (
    <>
      {!isEmbedded && (
        <>
          <BackButton onPress={() => setForm("phone")} position="absolute" />
          <Logo marginTop="$700" $lg={{ marginTop: "$600" }} flexShrink={0} />
        </>
      )}
      <Text
        size="$xxl"
        textAlign="center"
        semibold
        marginVertical="$400"
        marginTop={isEmbedded ? "$200" : "$400"}
      >
        Verify your phone
      </Text>
      <Text size="$md" textAlign="center" marginBottom="$200">
        Enter the verification code sent to your phone.
      </Text>
      <Text size="$md" textAlign="center" marginBottom="$500">
        {formatPhone(phone)}
        <Button.Wrapper
          aria-label="Edit phone number"
          onPress={() => setForm("phone")}
          display="inline"
        >
          <Pencil size="$150" marginLeft="$100" />
        </Button.Wrapper>
      </Text>
      <YStack position="relative" width="100%" maxWidth={400}>
        <FixedNumericInput
          value={otp}
          onChangeValue={setOtp}
          onSubmit={onContinue}
          borderColor={inputBorderColor}
        />
        <OTPStatus status={otpStatus} />
      </YStack>
      <Button
        marginTop="$400"
        marginBottom="$300"
        width="100%"
        onPress={onContinue}
        mode="primary"
        disabled={otp.length !== CODE_LENGTH}
        loading={disableInput}
      >
        Continue
      </Button>
      <Text size="$xs" color="$textSecondary" textAlign="center">
        Didn't receive a code? {resendElement}
      </Text>
    </>
  );
}
